// src/components/users/forms/EditUserForm.tsx
import React, { useState, useMemo } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import Select from "@/components/shared/ui/Select";
import LocationRow, {
  type LocationFormValue as CSCFormValue,
} from "@/components/shared/forms/LocationRow";

import {
  FiMail,
  FiPhone,
  FiShield,
  FiUser,
  FiCalendar,
  FiHash,
  FiHome,
  FiMapPin,
} from "react-icons/fi";

import { useRoles } from "@/api/features/roles/queries";
import type { Role as RoleModel } from "@/api/features/roles/types";

// Import canonical enums from users feature
import type { Gender, IdType } from "@/api/features/users/types";

// Local Role type — backend drives role names dynamically
export type Role = string;

/**
 * UI-level User type for the edit form.
 * - Mirrors what the UI needs (no password / otpChannel / userCode).
 * - Includes id + roleName for display.
 */
export type User = {
  id: number;

  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;

  roleId: number | null;
  roleName?: Role | null;

  gender?: Gender;
  dob?: string; // yyyy-mm-dd
  idType?: IdType;
  idNumber?: string;

  addressLine1?: string;
  addressLine2?: string;
  country?: string;
  state?: string;
  city?: string;
};

export default function EditUserForm({
  user,
  onSubmit,
  onResetPassword,
  submitting = false,
  onCancel,
  errorMessage,
}: {
  user: User;
  onSubmit: (payload: User) => void;
  onResetPassword?: () => void;
  submitting?: boolean;
  onCancel?: () => void;
  errorMessage?: string | null;
}) {
  const [form, setForm] = useState<User>({ ...user });
  const [localError, setLocalError] = useState<string | null>(null);

  const change = <K extends keyof User>(k: K, v: User[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (localError) setLocalError(null);
  };

  /* =========================
   * Dynamic Roles dropdown
   * ========================= */
  const { data, isLoading, isError, refetch } = useRoles({
    page: 1,
    pageSize: 100,
  });

  const roles: RoleModel[] = data?.items ?? [];

  const roleNameToId = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of roles) map.set(r.name, r.id);
    return map;
  }, [roles]);

  const roleIdToName = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of roles) map.set(r.id, r.name);
    return map;
  }, [roles]);

  const roleLabel =
    form.roleId != null
      ? roleIdToName.get(form.roleId) ?? ""
      : form.roleName ?? "";

  const roleOptions: string[] = useMemo(() => {
    if (isLoading) return ["Loading roles…"];
    if (isError) return ["Failed to load roles"];
    if (!roles.length) return ["No active roles"];
    // Only "system user" roles are assignable here. Section-managed roles
    // (Customer, Affiliate Partner, Supplier, …) are flagged isSystemRole=false
    // and created from their own dedicated sections instead.
    return roles.filter((r) => r.isSystemRole).map((r) => r.name);
  }, [isLoading, isError, roles]);

  const roleSelectDisabled = isLoading || isError || roles.length === 0;

  /* =========================
   * Submit / Cancel
   * ========================= */
  const submit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation similar to CreateUserForm (no password here)
    if (
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.username.trim() ||
      !form.email.trim()
    ) {
      setLocalError(
        "Please fill in first name, last name, username, and email."
      );
      return;
    }

    if (form.roleId == null) {
      setLocalError("Please select a role.");
      return;
    }

    // Normalize DOB so backend doesn't see ""
    const cleaned: User = {
      ...form,
      dob: form.dob && form.dob.trim() ? form.dob : undefined,
    };

    setLocalError(null);
    onSubmit(cleaned);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      history.back();
    }
  };

  const bannerMessage = localError || errorMessage || null;

  return (
    <>
      {/* header right slot is handled by parent; keep spacing consistent */}
      <div className="mb-4 flex items-center justify-end">
        {onResetPassword && (
          <button
            type="button"
            onClick={onResetPassword}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Reset password
          </button>
        )}
      </div>

      <form
        onSubmit={submit}
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        {/* Top error banner */}
        {bannerMessage && (
          <div
            className="sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            role="alert"
            aria-live="polite"
          >
            {bannerMessage}
          </div>
        )}

        {/* Name */}
        <Field label="First name" icon={<FiUser />}>
          <Input
            value={form.firstName}
            onChange={(e) => change("firstName", e.target.value)}
            placeholder="Enter first name"
            required
          />
        </Field>
        <Field label="Last name" icon={<FiUser />}>
          <Input
            value={form.lastName}
            onChange={(e) => change("lastName", e.target.value)}
            placeholder="Enter last name"
            required
          />
        </Field>

        {/* Username */}
        <Field label="Username" icon={<FiUser />}>
          <Input
            value={form.username}
            onChange={(e) => change("username", e.target.value)}
            placeholder="Unique username"
            required
          />
        </Field>

        {/* Contact */}
        <Field label="Email" icon={<FiMail />}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => change("email", e.target.value)}
            placeholder="name@company.com"
            required
          />
        </Field>
        <Field label="Phone" icon={<FiPhone />}>
          <PhoneInput
            country="in"
            value={form.phone}
            onChange={(v) => change("phone", `+${v}`)}
            inputClass="!w-full !rounded-lg !border !border-gray-300 !text-sm"
            buttonClass="!border-gray-300"
          />
        </Field>

        {/* Profile */}
        <Field label="Gender" icon={<FiUser />}>
          <Select
            value={form.gender || ""}
            onChange={(e) => change("gender", e.target.value as Gender)}
            options={["Male", "Female", "Other"]}
          />
        </Field>
        <Field label="Date of birth" icon={<FiCalendar />}>
          <Input
            type="date"
            value={form.dob ?? ""}
            onChange={(e) => change("dob", e.target.value)}
            placeholder="YYYY-MM-DD"
          />
        </Field>

        {/* Identity */}
        <Field label="Identification type" icon={<FiHash />}>
          <Select
            value={form.idType || ""}
            onChange={(e) => change("idType", e.target.value as IdType)}
            options={["National ID", "Passport"]}
          />
        </Field>
        <Field label="ID number" icon={<FiHash />}>
          <Input
            value={form.idNumber ?? ""}
            onChange={(e) => change("idNumber", e.target.value)}
            placeholder="Enter ID / Passport no."
          />
        </Field>

        {/* Role (dynamic from backend) */}
        <Field label="Role" icon={<FiShield />}>
          <div className="flex items-center gap-2">
            <Select
              value={roleLabel || ""}
              onChange={(e) => {
                const label = e.target.value;
                const id = roleNameToId.get(label) ?? null;
                setForm((p) => ({
                  ...p,
                  roleId: id,
                  roleName: label || p.roleName,
                }));
                if (localError) setLocalError(null);
              }}
              options={roleOptions}
              placeholder={
                isLoading
                  ? "Loading…"
                  : roles.length
                  ? "Select role"
                  : "No roles"
              }
              disabled={roleSelectDisabled}
            />
            {isError && (
              <button
                type="button"
                onClick={() => refetch()}
                className="text-xs text-sky-600 hover:underline"
              >
                Retry
              </button>
            )}
          </div>
        </Field>

        {/* Address */}
        <div className="mt-4 sm:col-span-2 text-sm font-semibold text-gray-800">
          Address &amp; Contact
        </div>
        <Field label="Address line 1" icon={<FiHome />}>
          <Input
            value={form.addressLine1 ?? ""}
            onChange={(e) => change("addressLine1", e.target.value)}
            placeholder="House / Flat / Street"
          />
        </Field>
        <Field label="Address line 2" icon={<FiMapPin />}>
          <Input
            value={form.addressLine2 ?? ""}
            onChange={(e) => change("addressLine2", e.target.value)}
            placeholder="Area / Landmark (optional)"
          />
        </Field>

        {/* Country → City */}
        <div className="sm:col-span-2">
          <LocationRow
            value={{
              country: form.country ?? undefined,
              state: form.state ?? undefined,
              city: form.city ?? undefined,
            }}
            onChange={(v: CSCFormValue) => {
              setForm((p) => ({
                ...p,
                country: v.country || "",
                state: v.state || "",
                city: v.city || "",
              }));
              if (localError) setLocalError(null);
            }}
            instancePrefix="edit-user-csc"
          />
        </div>

        {/* Actions */}
        <div className="mt-2 flex justify-end gap-2 sm:col-span-2">
          <button
            type="button"
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </>
  );
}
